import React from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import { ChargeType } from "@/pages/settings/pages/products-v2/types";
import {
  getBillingFrequencyLabel,
  getBillingFrequencyInMonths,
  formatBillingStartDate,
  formatCurrency
} from "../consts";
import { EqPaymentsBillingFrequency } from "@/pages/contracts/types";
import { useCurrencyConversion } from "@/services/hooks/useCurrencyConversion";
import ConvertedCurrency from "@/pages/deals/components/commercials/ConvertedCurrency";

interface SplitPaymentData {
  numberOfPayments: number;
  billingFrequencyLabel: string;
  billingFrequency: EqPaymentsBillingFrequency;
}

interface BillingSummaryProps {
  total: number;
  serviceDurationInMonths: number;
  billingFrequency: number | null;
  billingStartDate: string;
  chargeType?: ChargeType;
  splitPaymentData?: SplitPaymentData | null;
  currency?: string | null;
}

const BillingSummary: React.FC<BillingSummaryProps> = ({
  total,
  serviceDurationInMonths,
  billingFrequency,
  billingStartDate,
  chargeType,
  splitPaymentData,
  currency: rawCurrency
}) => {
  const { t } = useTranslation();

  const isOneTimeCharge = chargeType === ChargeType.ONE_TIME;

  const numberOfPayments = splitPaymentData
    ? splitPaymentData.numberOfPayments
    : isOneTimeCharge
      ? 1
      : (() => {
          const frequencyInMonths =
            getBillingFrequencyInMonths(billingFrequency);
          if (frequencyInMonths === 0) return 1;
          return Math.max(
            1,
            Math.ceil(serviceDurationInMonths / frequencyInMonths)
          );
        })();
  const paymentAmount = total / numberOfPayments;
  const paymentAmountDecimals = Number.isInteger(paymentAmount) ? 0 : 2;
  const billingFrequencyLabel = splitPaymentData
    ? splitPaymentData.billingFrequencyLabel
    : getBillingFrequencyLabel(billingFrequency, t);
  const formattedStartDate = formatBillingStartDate(billingStartDate);

  // recordCurrencySignOverride is the contract's sign already resolved against the
  // account's, for the figures printed beside a ConvertedCurrency below.
  const {
    accountCurrencySymbol,
    recordCurrencySignOverride,
    showConversion,
    rate
  } =
    // The raw prop: defaulting before the comparison makes an unset contract
    // currency read as USD and offers a conversion against it.
    useCurrencyConversion(rawCurrency);
  const currency = rawCurrency ?? "USD";

  return (
    <div className="flex gap-6 items-start w-full">
      <div className="flex gap-1 h-8 items-center px-0 py-1.5 w-25">
        <Text variant="bodySecondary">{t("contractTerms.summary")}</Text>
      </div>
      <div className="flex grow flex-col items-end gap-0.5 p-1.5">
        {billingStartDate ? (
          <>
            <div className="flex items-center gap-1 flex-wrap justify-end">
              <Text variant="body">
                {t("contractTerms.paymentSchedule.paymentsOfAmount", {
                  count: numberOfPayments,
                  amount: formatCurrency(
                    paymentAmount,
                    currency,
                    paymentAmountDecimals,
                    recordCurrencySignOverride
                  )
                })}
              </Text>
              {showConversion && paymentAmount > 0 && (
                <ConvertedCurrency
                  amount={paymentAmount}
                  rate={rate}
                  accountCurrencySymbol={accountCurrencySymbol}
                />
              )}
            </div>
            {isOneTimeCharge && !splitPaymentData ? (
              <Text variant="body">
                {t("contractTerms.billedOnce", { date: formattedStartDate })}
              </Text>
            ) : (
              <Text variant="body">
                {t("contractTerms.billedFrequency", {
                  frequency: billingFrequencyLabel.toLowerCase(),
                  date: formattedStartDate
                })}
              </Text>
            )}
          </>
        ) : (
          <div className="h-4 w-4 text-text-secondary">—</div>
        )}
      </div>
    </div>
  );
};

export default BillingSummary;
